/**
 * LabResultsViewer Component
 * 
 * Patient-facing lab results viewer accessed via PatientActionLink.
 * Shows list of available lab results with ability to view details and download PDFs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FlaskConical, 
  FileText, 
  Download, 
  ChevronRight,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/services/api';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { isRawId } from '@/lib/labUtils';

interface LabResult {
  id: string;
  panel_name: string;
  status: string;
  collection_date?: string;
  result_date?: string;
  created_at: string;
  markers?: LabMarker[];
}

interface LabMarker {
  name: string;
  value: number | string;
  unit: string;
  reference_range?: string;
  status?: 'normal' | 'high' | 'low' | 'critical';
}

interface LabResultsViewerProps {
  sessionToken: string;
  patientId: string;
  clinicName: string;
  patientName?: string;
}

const LabResultsViewer: React.FC<LabResultsViewerProps> = ({
  sessionToken,
  patientId,
  clinicName,
  patientName,
}) => {
  const [results, setResults] = useState<LabResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<LabResult | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  const getHeaders = useCallback(() => ({
    'x-session-token': sessionToken,
  }), [sessionToken]);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/labs/public/results', {
        headers: getHeaders(),
      });

      if (response.data.success) {
        setResults(response.data.data.results || []);
      } else {
        setError(response.data.error?.message || 'Failed to load results');
      }
    } catch (err: any) {
      console.error('Error loading lab results:', err);
      setError(err.response?.data?.error?.message || 'Failed to load lab results');
    } finally {
      setIsLoading(false);
    }
  };

  const loadResultDetails = async (resultId: string) => {
    try {
      setIsLoadingDetails(true);

      const response = await apiClient.get(`/labs/public/results/${resultId}`, {
        headers: getHeaders(),
      });

      if (response.data.success) {
        setSelectedResult(response.data.data.result);
      } else {
        toast({
          title: 'Error',
          description: response.data.error?.message || 'Failed to load result details',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Error loading result details:', err);
      toast({
        title: 'Error',
        description: 'Failed to load result details',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const downloadPDF = async (resultId: string) => {
    try {
      setIsDownloading(true);

      const response = await apiClient.get(`/labs/public/results/${resultId}/pdf`, {
        headers: getHeaders(),
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lab-results-${resultId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download Complete',
        description: 'Lab results PDF downloaded successfully.',
      });
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      toast({
        title: 'Download Failed',
        description: err?.message || 'Unable to download PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', label: string }> = {
      completed: { variant: 'default', label: 'Completed' },
      reviewed: { variant: 'default', label: 'Reviewed' },
      finalized: { variant: 'default', label: 'Finalized' },
      pending: { variant: 'secondary', label: 'Pending' },
      processing: { variant: 'secondary', label: 'Processing' },
    };

    const config = statusConfig[status] || { variant: 'outline', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getMarkerStatusIcon = (status?: string) => {
    switch (status) {
      case 'high':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'low':
        return <ArrowDownRight className="w-4 h-4 text-blue-500" />;
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Minus className="w-4 h-4 text-green-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-2xl mx-auto border-slate-700 bg-slate-800">
          <CardHeader>
            <Skeleton className="h-8 w-48 bg-slate-700" />
            <Skeleton className="h-4 w-64 bg-slate-700 mt-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 w-full bg-slate-700" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 flex items-center justify-center">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800 text-center">
          <CardHeader>
            <AlertCircle className="w-16 h-16 mx-auto text-red-500" />
            <CardTitle className="text-white mt-4">Error Loading Results</CardTitle>
            <CardDescription className="text-slate-400">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={loadResults} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/novamed_logo_white.svg" alt="NovaMed" className="mx-auto h-8 mb-2" />
          <p className="text-slate-400 text-sm">{clinicName}</p>
        </div>

        <Card className="border-slate-700 bg-slate-800 text-white">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
                <FlaskConical className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <CardTitle className="text-xl">Your Lab Results</CardTitle>
                <CardDescription className="text-slate-400">
                  {patientName && `Results for ${patientName}`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {results.length === 0 ? (
              <div className="text-center py-12">
                <FlaskConical className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">No lab results available yet.</p>
                <p className="text-sm text-slate-500 mt-2">
                  Results will appear here once they are processed.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map(result => (
                  <div
                    key={result.id}
                    className="border border-slate-700 rounded-lg p-4 hover:border-teal-500/50 transition-colors cursor-pointer"
                    onClick={() => loadResultDetails(result.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-white">
                            {!isRawId(result.panel_name) ? result.panel_name : 'Lab Panel'}
                          </h3>
                          {getStatusBadge(result.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          {result.collection_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Collected: {format(parseISO(result.collection_date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {result.result_date && (
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Results: {format(parseISO(result.result_date), 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>

                    {/* Quick actions */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadResultDetails(result.id);
                        }}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadPDF(result.id);
                        }}
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Download PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Important notice */}
        <div className="mt-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <p className="text-sm text-amber-300">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            For questions about your results, please contact your healthcare provider.
          </p>
        </div>
      </div>

      {/* Result Details Dialog */}
      <Dialog open={!!selectedResult} onOpenChange={(open) => !open && setSelectedResult(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-800 text-white border-slate-700">
          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
            </div>
          ) : selectedResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-white">
                  <FlaskConical className="w-5 h-5 text-teal-400" />
                  {!isRawId(selectedResult.panel_name) ? selectedResult.panel_name : 'Lab Results'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Result info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Collection Date</p>
                    <p className="font-medium">
                      {selectedResult.collection_date
                        ? format(parseISO(selectedResult.collection_date), 'MMMM d, yyyy')
                        : 'Not available'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400">Results Date</p>
                    <p className="font-medium">
                      {selectedResult.result_date
                        ? format(parseISO(selectedResult.result_date), 'MMMM d, yyyy')
                        : 'Not available'}
                    </p>
                  </div>
                </div>

                <Separator className="bg-slate-700" />

                {/* Markers */}
                {selectedResult.markers && selectedResult.markers.length > 0 ? (
                  <div className="space-y-2">
                    <h4 className="font-semibold">Test Results</h4>
                    <div className="space-y-2">
                      {selectedResult.markers.map((marker, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            'p-3 rounded-lg border',
                            marker.status === 'critical' ? 'border-red-500 bg-red-500/10' :
                            marker.status === 'high' || marker.status === 'low' ? 'border-amber-500 bg-amber-500/10' :
                            'border-slate-700 bg-slate-900/50'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getMarkerStatusIcon(marker.status)}
                              <span className="font-medium">{marker.name}</span>
                            </div>
                            <div className="text-right">
                              <span className="font-semibold">
                                {marker.value} {marker.unit}
                              </span>
                              {marker.reference_range && (
                                <p className="text-xs text-slate-400">
                                  Reference: {marker.reference_range}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-4">
                    Detailed results not available. Download the PDF for full information.
                  </p>
                )}

                {/* Download button */}
                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  onClick={() => downloadPDF(selectedResult.id)}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download Full Report (PDF)
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabResultsViewer;








